import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
// API
import { useQuery } from "@tanstack/react-query";
import { gameDefinitionsQueryOpts } from "@src/services/apiQueries/cdn";
import { useCurrentLanguageTag } from "@config/i18n/languageOptions";
// UI
import { Stack, Group, ScrollArea, Table, Avatar } from "@mantine/core";
import { DonutChart } from "@mantine/charts";
// types
import { type GameStat } from "../helpers";
import type { GameDefinitionMap } from "@neuromotion-inc/mightier-ts-sdk";

interface GamesPlayedTableProps {
  gameStats: GameStat[];
}

const COLORS = [
  "dark",
  "gray",
  "red",
  "pink",
  "grape",
  "violet",
  "indigo",
  "blue",
  "cyan",
  "green",
  "lime",
  "yellow",
  "orange",
  "teal",
] as const;

export type Color = (typeof COLORS)[number];

// Simple deterministic hash
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0; // unsigned int
  }
  return hash;
}

export function pickColor(value: string): Color {
  const hash = hashString(value);
  return COLORS[hash % COLORS.length] ?? "red";
}

interface GameRowProps {
  gameStat: GameStat;
  gameDefs: GameDefinitionMap;
}

const GameRow: React.FC<GameRowProps> = (props) => {
  const { gameStat, gameDefs } = props;

  const defForGame = gameDefs[gameStat.gameKey];

  return (
    <Table.Tr>
      <Table.Td>
        <Group>
          <Avatar src={defForGame.iconUrl} />
          {defForGame.displayName}
        </Group>
      </Table.Td>
      <Table.Td>{Math.ceil(gameStat.secondsPlayed / 60)}</Table.Td>
      <Table.Td>{gameStat.numCooldowns}</Table.Td>
    </Table.Tr>
  );
};

export const GamesPlayedChart: React.FC<GamesPlayedTableProps> = (props) => {
  const { t } = useTranslation();
  const { language } = useCurrentLanguageTag();
  const { data: gameDefs } = useQuery(gameDefinitionsQueryOpts(language));

  const pieChartDataCombined = useMemo(() => {
    if (!gameDefs) {
      return [];
    }

    return props.gameStats.map((stat) => {
      return {
        name: gameDefs[stat.gameKey].displayName,
        value: {
          playtime: Math.round(stat.secondsPlayed / 60),
          cooldowns: stat.numCooldowns,
        },
        color: pickColor(stat.gameKey),
      };
    });
  }, [props.gameStats, gameDefs]);

  return (
    <Stack w="100%">
      <Group justify="space-around">
        <DonutChart
          chartLabel={t("playerInsights.gamesPlayedSection.playtimeDonutLabel")}
          data={pieChartDataCombined.map((d) => {
            return { ...d, value: d.value.playtime };
          })}
        />
        <DonutChart
          chartLabel={t("playerInsights.gamesPlayedSection.cooldownDonutLabel")}
          data={pieChartDataCombined.map((d) => {
            return { ...d, value: d.value.cooldowns };
          })}
        />
      </Group>

      <ScrollArea h="300">
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>
                {t("playerInsights.gamesPlayedSection.tabelGameNameCol")}
              </Table.Th>
              <Table.Th>
                {t("playerInsights.gamesPlayedSection.tableMinutesCol")}
              </Table.Th>
              <Table.Th>
                {t("playerInsights.gamesPlayedSection.tableCooldownsCol")}
              </Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {gameDefs
              ? props.gameStats.map((stat, idx) => (
                  <GameRow gameStat={stat} gameDefs={gameDefs} key={idx} />
                ))
              : null}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </Stack>
  );
};
